
// @ts-nocheck
/* eslint-disable */
import {
  defineComponent,
  inject,
  computed,
  watch,
  nextTick,
  getCurrentInstance,
  ref,
  toRef,
  toRefs,
  isReactive,
  shallowRef,
  onMounted,
  onUpdated,
} from 'vue'

import { useAttrs } from '@/components/hooks'
import {
  UPDATE_MODEL_EVENT,
  VALIDATE_STATE_MAP,
} from '@/components/utils/constants'
import {
  autoprefixer,
  isObject,
  useGlobalConfig,
} from '@/components/utils/util'
import isServer from '@/components/utils/isServer'
import { isKorean } from '@/components/utils/isDef'
import { isValidComponentSize } from '@/components/utils/validators'
// import { elFormKey, elFormItemKey } from '@/components/form'
import calcTextareaHeight from './calcTextareaHeight'

import type { PropType } from 'vue'
// import type { ElFormContext, ElFormItemContext } from '@/components/form'

type AutosizeProp =
  | {
      minRows?: number
      maxRows?: number
    }
  | boolean

const PENDANT_MAP = {
  suffix: 'append',
  prefix: 'prepend',
}

const showPwdIcon = require('./img/ic_Eye_Open_16.svg')
const hidePwdIcon = require('./img/ic_Eye_Close_16.svg')

export default defineComponent({
  name: 'ElInput',

  inheritAttrs: false,

  props: {
    modelValue: {
      type: [String, Number],
      default: '',
    },
    type: {
      type: String,
      default: 'text',
    },
    size: {
      type: String as PropType<ComponentSize>,
      validator: isValidComponentSize,
    },
    resize: {
      type: String as PropType<'none' | 'both' | 'horizontal' | 'vertical'>,
      validator: (val: string) =>
        ['none', 'both', 'horizontal', 'vertical'].includes(val),
    },
    autosize: {
      type: [Boolean, Object] as PropType<AutosizeProp>,
      default: false,
    },
    autocomplete: {
      type: String,
      default: 'off',
      validator: (val: string) => ['on', 'off'].includes(val),
    },
    placeholder: {
      type: String,
    },
    form: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    showPassword: {
      type: Boolean,
      default: false,
    },
    showPasswordState: {
      type: Boolean,
      default: false,
    },
    showWordLimit: {
      type: Boolean,
      default: false,
    },
    suffixIcon: {
      type: String,
      default: '',
    },
    prefixIcon: {
      type: String,
      default: '',
    },
    label: {
      type: String,
    },
    tabindex: {
      type: String,
    },
    validateEvent: {
      type: Boolean,
      default: true,
    },
    centerText: Boolean,
  },

  emits: [
    UPDATE_MODEL_EVENT,
    'input',
    'change',
    'focus',
    'blur',
    'clear',
    'mouseleave',
    'mouseenter',
    'keydown',
  ],

  setup(props, ctx) {
    const instance = getCurrentInstance()
    const attrs = useAttrs()
    const $ELEMENT = useGlobalConfig()

    // const elForm = inject(elFormKey, {} as ElFormContext)
    // const elFormItem = inject(elFormItemKey, {} as ElFormItemContext)

    const input = ref(null)
    const textarea = ref(null)
    const focused = ref(false)
    const hovering = ref(false)
    const isComposing = ref(false)
    const passwordVisible = ref(props.showPasswordState)
    const _textareaCalcStyle = shallowRef({})
    let autosizeWatchStop = null

    const inputOrTextarea = computed(() => input.value || textarea.value)
    const inputSize = computed(() => props.size || $ELEMENT.size)
    const needStatusIcon = false // computed(() => elForm.statusIcon)
    const validateState = false // computed(() => elFormItem.validateState || '')
    const validateIcon = false // computed(() => VALIDATE_STATE_MAP[validateState.value])
    const textareaStyle = computed(() => ({
      ..._textareaCalcStyle.value,
      resize: props.resize,
    }))
    const inputDisabled = computed(() => props.disabled)
    const nativeInputValue = computed(() =>
      props.modelValue === null || props.modelValue === undefined
        ? ''
        : String(props.modelValue)
    )
    const upperLimit = computed(() => ctx.attrs.maxlength)
    const showClear = computed(() => {
      return (
        props.clearable &&
        !inputDisabled.value &&
        !props.readonly &&
        nativeInputValue.value &&
        (focused.value || hovering.value)
      )
    })
    const showPwdVisible = computed(() => {
      return (
        props.showPassword &&
        !inputDisabled.value &&
        !props.readonly &&
        (!!nativeInputValue.value || focused.value)
      )
    })

    const pwdIconSrc = computed(() => {
      return passwordVisible.value ? hidePwdIcon : showPwdIcon
    })
    const isWordLimitVisible = computed(() => {
      return (
        props.showWordLimit &&
        ctx.attrs.maxlength &&
        (props.type === 'text' || props.type === 'textarea') &&
        !inputDisabled.value &&
        !props.readonly &&
        !props.showPassword
      )
    })
    const textLength = computed(() => {
      return typeof props.modelValue === 'number'
        ? String(props.modelValue).length
        : (props.modelValue || '').length
    })
    const inputExceed = computed(() => {
      // show exceed style if length of initial value greater then maxlength
      return (
        isWordLimitVisible.value &&
        ((textLength.value > upperLimit.value) as number)
      )
    })

    const resizeTextarea = () => {
      const { type, autosize } = props

      if (isServer || type !== 'textarea') return

      if (autosize) {
        // eslint-disable-next-line no-void
        const minRows = isObject(autosize) ? autosize.minRows : void 0
        // eslint-disable-next-line no-void
        const maxRows = isObject(autosize) ? autosize.maxRows : void 0
        _textareaCalcStyle.value = calcTextareaHeight(
          textarea.value,
          minRows,
          maxRows
        )
      } else {
        _textareaCalcStyle.value = {
          minHeight: calcTextareaHeight(textarea.value).minHeight,
        }
      }
    }

    const watchAutosize = () => {
      const autosizeRef = toRef(props, 'autosize')

      if (autosizeRef.value instanceof Object && !isReactive(autosizeRef.value)) {
        autosizeWatchStop = watch(autosizeRef, () => {
          resizeTextarea()
        })
      } else {
        autosizeWatchStop = watch(autosizeRef.value, () => {
          resizeTextarea()
        })
      }
    }

    watchAutosize()

    const setNativeInputValue = () => {
      const input = inputOrTextarea.value as any
      if (!input || input.value === nativeInputValue.value) return
      input.value = nativeInputValue.value
    }

    const calcIconOffset = (place: any) => {
      const { el } = instance?.vnode
      const elList: HTMLSpanElement[] = Array.from(
        el?.querySelectorAll(`.el-input__${place}`)
      )
      const target = elList.find((item) => item.parentNode === el)

      if (!target) return

      const pendant = PENDANT_MAP[place]

      if (ctx.slots[pendant]) {
        target.style.transform = `translateX(${place === 'suffix' ? '-' : ''}${
          el.querySelector(`.el-input-group__${pendant}`).offsetWidth
        }px)`
      } else {
        target.removeAttribute('style')
      }
    }

    const updateIconOffset = () => {
      calcIconOffset('prefix')
      calcIconOffset('suffix')
    }

    const handleInput = (event: any) => {
      const { value } = event.target

      // should not emit input during composition
      // see: https://github.com/ElemeFE/element/issues/10516
      if (isComposing.value) return

      // hack for https://github.com/ElemeFE/element/issues/8548
      // should remove the following line when we don't support IE
      if (value === nativeInputValue.value) return

      ctx.emit(UPDATE_MODEL_EVENT, value)
      ctx.emit('input', value)

      // ensure native input value is controlled
      // see: https://github.com/ElemeFE/element/issues/12850
      nextTick(setNativeInputValue)
    }

    const handleChange = (event: any) => {
      ctx.emit('change', event.target.value)
    }

    const focus = () => {
      // see: https://github.com/ElemeFE/element/issues/18573
      nextTick(() => {
        inputOrTextarea.value.focus()
      })
    }

    const blur = () => {
      inputOrTextarea.value.blur()
    }

    const handleFocus = (event: any) => {
      focused.value = true
      ctx.emit('focus', event)
    }

    const handleBlur = (event: any) => {
      focused.value = false
      ctx.emit('blur', event)
      // if (props.validateEvent) {
      //   elFormItem.formItemMitt?.emit('el.form.blur', [props.modelValue])
      // }
    }

    const select = () => {
      inputOrTextarea.value?.select()
    }

    const handleCompositionStart = () => {
      isComposing.value = true
    }

    const handleCompositionUpdate = (event: any) => {
      const text = event.target.value
      const lastCharacter = text[text.length - 1] || ''
      isComposing.value = !isKorean(lastCharacter)
    }

    const handleCompositionEnd = (event: any) => {
      if (isComposing.value) {
        isComposing.value = false
        handleInput(event)
      }
    }

    const clear = () => {
      ctx.emit(UPDATE_MODEL_EVENT, '')
      ctx.emit('change', '')
      ctx.emit('clear')
    }

    const handlePasswordVisible = () => {
      passwordVisible.value = !passwordVisible.value
      focus()
    }

    const getSuffixVisible = () => {
      return (
        ctx.slots.suffix ||
        props.suffixIcon ||
        showClear.value ||
        props.showPassword ||
        isWordLimitVisible.value ||
        (validateState.value && needStatusIcon.value)
      )
    }

    watch(
      () => props.modelValue,
      (val) => {
        nextTick(resizeTextarea)
        // if (props.validateEvent) {
        //   elFormItem.formItemMitt?.emit('el.form.change', [val])
        // }
      }
    )

    // native input value is set explicitly
    // do not use v-model / :value in template
    // see: https://github.com/ElemeFE/element/issues/14521
    watch(nativeInputValue, () => {
      setNativeInputValue()
    })



    // when change between <input> and <textarea>,
    // update DOM dependent value and styles
    // https://github.com/ElemeFE/element/issues/14857
    watch(
      () => props.type,
      () => {
        nextTick(() => {
          setNativeInputValue()
          resizeTextarea()
          updateIconOffset()
        })
      }
    )

    onMounted(() => {
      setNativeInputValue()
      updateIconOffset()
      nextTick(resizeTextarea)
    })

    onUpdated(() => {
      nextTick(updateIconOffset)
    })

    const onMouseLeave = (e: any) => {
      hovering.value = false
      ctx.emit('mouseleave', e)
    }

    const onMouseEnter = (e: any) => {
      hovering.value = true
      ctx.emit('mouseenter', e)
    }

    const handleKeydown = (e: any) => {
      ctx.emit('keydown', e)
    }

    return {
      input,
      textarea,
      attrs,
      inputSize,
      validateState,
      validateIcon,
      textareaStyle,
      resizeTextarea,
      inputDisabled,
      showClear,
      showPwdVisible,
      isWordLimitVisible,
      upperLimit,
      textLength,
      hovering,
      inputExceed,
      passwordVisible,
      pwdIconSrc,
      inputOrTextarea,
      handleInput,
      handleChange,
      handleFocus,
      handleBlur,
      handleCompositionStart,
      handleCompositionUpdate,
      handleCompositionEnd,
      handlePasswordVisible,
      clear,
      select,
      focus,
      blur,
      getSuffixVisible,
      onMouseLeave,
      onMouseEnter,
      handleKeydown,
    }
  },
})
